import { defineStore } from "pinia";

const USERS_STORAGE_KEY = "blade:users";
const SLIDE_INFO_STORAGE_KEY = "blade:slideInfo";
const READ_ONLY_STORAGE_KEY = "blade:isReadOnly";
const SLIDE_SELECTED_STORAGE_KEY = "blade:isSlideSelected";

type User = string;

function loadUsersFromStorage(): User[] {
  try {
    const raw = localStorage.getItem(USERS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is User => typeof value === "string" && value.length > 0);
  } catch {
    return [];
  }
}

function loadSlideInfo(): SlideInfo {
  try {
    const raw = sessionStorage.getItem(SLIDE_INFO_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function loadIsReadOnly(): boolean {
  try {
    return sessionStorage.getItem(READ_ONLY_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function loadIsSlideSelected(): boolean {
  try {
    return sessionStorage.getItem(SLIDE_SELECTED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

type SlideInfo = Record<string, unknown>;

type ConfigState = {
  slideInfo: SlideInfo;
  isSlideSelected: boolean;
  cellGroupID: string;
  isLogin: boolean;
  logoutFlag: boolean;
  slideTextReport: string;
  isBusy: boolean;
  isReadOnly: boolean;
  users: User[];
};

function getDefaultState(): ConfigState {
  return {
    slideInfo: loadSlideInfo(),
    isSlideSelected: loadIsSlideSelected(),
    cellGroupID: "",
    isLogin: false,
    logoutFlag: false,
    slideTextReport: "",
    isBusy: false,
    isReadOnly: loadIsReadOnly(),
    users: loadUsersFromStorage(),
  };
}

export const useConfigStore = defineStore("config", {
  state: (): ConfigState => getDefaultState(),
  actions: {
    setIsReadOnly(readOnly: boolean) {
      this.isReadOnly = readOnly;
      try { sessionStorage.setItem(READ_ONLY_STORAGE_KEY, String(readOnly)); } catch { /* ignore */ }
    },
    setSlideInfo(slideInfo: Record<string, unknown>) {
      this.slideInfo = slideInfo;
      try { sessionStorage.setItem(SLIDE_INFO_STORAGE_KEY, JSON.stringify(slideInfo)); } catch { /* ignore */ }
    },
    updateSlideInfoKey(key: string, value: unknown) {
      this.slideInfo[key] = value;
      try { sessionStorage.setItem(SLIDE_INFO_STORAGE_KEY, JSON.stringify(this.slideInfo)); } catch { /* ignore */ }
    },
    setIsSlideSelected(isSelected: boolean) {
      this.isSlideSelected = isSelected;
      try { sessionStorage.setItem(SLIDE_SELECTED_STORAGE_KEY, String(isSelected)); } catch { /* ignore */ }
    },
    setCellGroupID(ID: string) {
      this.cellGroupID = ID;
    },
    setIsLogin(isLogin: boolean) {
      this.isLogin = isLogin;
    },
    toggleLogout() {
      this.logoutFlag = !this.logoutFlag;
    },
    setIsBusy(isBusy: boolean) {
      this.isBusy = isBusy;
    },
    setUsers(users: unknown[]) {
      const normalized = Array.isArray(users)
        ? users.filter((value): value is User => typeof value === "string" && value.length > 0)
        : [];
      this.users = normalized;
      try {
        localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(this.users));
      } catch {
        // ignore storage errors
      }
    },
    removeUser(displayName: string) {
      this.users = this.users.filter(u => u !== displayName);
      try {
        localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(this.users));
      } catch {
        // ignore storage errors
      }
    },
    addUser(displayName: string) {
      if (!this.users.includes(displayName)) {
        this.users = [...this.users, displayName].sort();
        try {
          localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(this.users));
        } catch {
          // ignore storage errors
        }
      }
    },
    setSlideTextReport(report: string) {
      this.slideTextReport = report;
    },
    resetState() {
      const { isLogin, logoutFlag } = this;
      try {
        localStorage.clear();
        sessionStorage.removeItem(SLIDE_INFO_STORAGE_KEY);
        sessionStorage.removeItem(READ_ONLY_STORAGE_KEY);
        sessionStorage.removeItem(SLIDE_SELECTED_STORAGE_KEY);
      } catch {
        // ignore storage errors
      }
      const defaults = getDefaultState();
      Object.assign(this, defaults);
      this.isLogin = isLogin;
      this.logoutFlag = logoutFlag;
    },
  },
});

// Export Pinia store
